import { useState, useCallback } from 'react';
import axios from 'axios';
import { appConfig } from 'src/config/appConfig';
import { useAuth } from './useAuth';
import type { RaffleWinner } from 'src/types/raffle';

export function useRaffleActions() {
  const { accessToken, userId } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    'X-Api-Key': process.env.REACT_APP_API_KEY as string,
  };

  const buyTickets = useCallback(
    async (raffleId: string, quantity: number): Promise<boolean> => {
      if (!userId) return false;
      setIsLoading(true);
      setError(null);
      try {
        await axios.post(
          `${appConfig.serviceUrl}/raffles/${raffleId}/tickets`,
          { userId, quantity },
          { headers },
        );
        setIsLoading(false);
        return true;
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to buy tickets';
        setError(msg);
        setIsLoading(false);
        return false;
      }
    },
    [userId, headers],
  );

  const toggleFavorite = useCallback(
    async (raffleId: string): Promise<{ favorited: boolean } | null> => {
      if (!userId) return null;
      try {
        const res = await axios.post(
          `${appConfig.serviceUrl}/raffles/${raffleId}/favorite`,
          { userId },
          { headers },
        );
        return res.data;
      } catch {
        return null;
      }
    },
    [userId, headers],
  );

  const reportRaffle = useCallback(
    async (raffleId: string, reason: string): Promise<boolean> => {
      if (!userId) return false;
      try {
        await axios.post(
          `${appConfig.serviceUrl}/raffles/${raffleId}/report`,
          { userId, reason },
          { headers },
        );
        return true;
      } catch {
        return false;
      }
    },
    [userId, headers],
  );

  const getWinners = useCallback(
    async (raffleId: string): Promise<RaffleWinner[]> => {
      try {
        const res = await axios.get(
          `${appConfig.serviceUrl}/raffles/${raffleId}/winners`,
          { headers },
        );
        return res.data;
      } catch {
        return [];
      }
    },
    [headers],
  );

  const getUserTicketCount = useCallback(
    async (raffleId: string): Promise<number> => {
      if (!userId) return 0;
      try {
        const res = await axios.get(
          `${appConfig.serviceUrl}/raffles/${raffleId}/tickets?page=1&limit=1000`,
          { headers },
        );
        const tickets = res.data?.data || [];
        return tickets
          .filter((t: any) => t.buyerId === userId)
          .reduce((sum: number, t: any) => sum + (t.quantity || 0), 0);
      } catch {
        return 0;
      }
    },
    [userId, headers],
  );

  return { buyTickets, toggleFavorite, reportRaffle, getWinners, getUserTicketCount, isLoading, error };
}
